import type { StatusTone } from '../../components/common/StatusTag';

/**
 * Shared status → badge-class map.
 *
 * DEPRECATED for new code — prefer `<StatusTag tone={…}>` (see PresenceTag / DayTypeTag
 * below). Tailwind v3 drops the `/15` opacity modifier on `var()`-defined colours, so the
 * tinted background these classes ask for never compiles; StatusTag mixes the colour
 * inline instead. Kept only so any straggling caller still gets legible text.
 */
export const STATUS_BADGE: Record<string, string> = {
  present: 'text-success',
  half_day: 'text-warning',
  partial: 'text-info',
  absent: 'bg-muted text-muted-foreground',
};

/**
 * `attendance_days.status` — how the backend classifies a finished day:
 *   present   ≥ full_day_minutes  (480 by default)
 *   half_day  ≥ half_day_minutes  (240 by default)
 *   partial   seen, but under the half-day threshold
 *   absent    never seen
 */
export type DayStatus = 'present' | 'half_day' | 'partial' | 'absent' | (string & {});

/** Was the employee seen on site at all that day? */
export function isPresentDay(status?: string | null): boolean {
  return !!status && status !== 'absent';
}

/* ── Daily views (Presence board, Timeline) ──────────────────────────────────
 * Here the only question is "are they in today?", so a day is Present or Absent.
 * The full / half / partial split is a payroll classification of a COMPLETED day —
 * against someone who walked in twenty minutes ago it reads as a fault ("PARTIAL")
 * rather than a fact. It belongs to the monthly consolidation, below.
 */

export function presenceLabel(status?: string | null): string {
  return isPresentDay(status) ? 'Present' : 'Absent';
}

export function presenceTone(status?: string | null): StatusTone {
  return isPresentDay(status) ? 'success' : 'neutral';
}

/* ── Monthly consolidated views ───────────────────────────────────────────────
 * Day-type vocabulary: Full day / Half day / Absent. `partial` earns no day credit
 * (the backend's payable_days counts it as zero, same as absent) so it is reported as
 * Absent, with the raw classification kept in the badge's tooltip so the day isn't
 * silently rewritten.
 */

const CONSOLIDATED_LABEL: Record<string, string> = {
  present: 'Full day',
  half_day: 'Half day',
  partial: 'Absent',
  absent: 'Absent',
};

/** Fixed display order for the consolidated summary, so chips never reshuffle. */
export const CONSOLIDATED_BUCKETS = ['present', 'half_day', 'absent'] as const;
export type ConsolidatedBucket = (typeof CONSOLIDATED_BUCKETS)[number];

export function consolidatedLabel(status?: string | null): string {
  return CONSOLIDATED_LABEL[status || 'absent'] || 'Absent';
}

export function consolidatedTone(status?: string | null): StatusTone {
  if (status === 'present') return 'success';
  if (status === 'half_day') return 'warning';
  return 'neutral';
}

/** Extra detail for a day whose displayed label hides its raw classification. */
export function consolidatedHint(status?: string | null): string | undefined {
  return status === 'partial'
    ? 'Seen on site but below the half-day threshold — earns no day credit'
    : undefined;
}

/**
 * Fold a `{ status: { days, minutes } }` summary onto the three consolidated buckets,
 * merging `partial` into `absent`.
 */
export function consolidateSummary(
  summary: Record<string, { days?: number; minutes?: number }> | null | undefined,
): Record<ConsolidatedBucket, number> {
  const out = { present: 0, half_day: 0, absent: 0 } as Record<ConsolidatedBucket, number>;
  for (const [status, v] of Object.entries(summary || {})) {
    const bucket: ConsolidatedBucket = status === 'present' ? 'present' : status === 'half_day' ? 'half_day' : 'absent';
    out[bucket] += v?.days || 0;
  }
  return out;
}

export function fmtTime(ts?: string | null): string {
  if (!ts) return '—';
  const d = new Date(ts);
  return isNaN(d.getTime()) ? '—' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function fmtDuration(minutes?: number | null): string {
  if (!minutes || minutes <= 0) return '0h 0m';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
}
